import React, { MutableRefObject, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { useModal } from 'hooks/useModal';
import { Stack } from '@mui/material';
import { useQuery } from '@apollo/client';
import { GraphqlResolverInput, ModalsKeys, Nullable, QueryResponse } from 'global';
import { Equipment } from 'modules/desking/types';
import { FormProvider, useWatch } from 'react-hook-form';
import { useEquipmentAssets } from 'modules/equipment/api';
import { ChargeBucketRef } from 'modules/equipment/types';
import { Listing, useListing } from 'components/shared/TableListing';
import { QuoteCalcPreviewInput } from '@trnsact/trnsact-shared-types';
import { ListingForm, Group } from 'components/shared/TableListing/types';
import { useEquipmentActions, getCalculatedTotalsByLevel } from 'modules/equipment';

import { quoteAPI } from '../../../api';
import { GroupSubtotalRow } from './cells';
import { ChargeListingService } from '../../../lib';
import { DealerProfitability } from './DealerProfitability';
import { quoteMapper, quoteCalcMapper } from '../../../mappers';
import { GroupHeader, groupHeaderCellStyles } from './GroupHeader';
import { invoiceDetailsListingColumns } from './invoiceDetailsListingConfig';
import { QuoteCalcViewModel } from '../../../api/types';
import {
  Quote,
  InvoiceListingActions,
  CalculatedTotalsByLevel,
  QuoteChargeListingEntity,
  QuoteEquipmentWithCharge,
  QuoteChargeListingContext,
} from '../../../types';

interface Props {
  VOId: string;
  dynamicsVOId: string;
  quote: Nullable<Quote>;
  equipmentList: Equipment[];
  showDealerProfitability?: boolean;
  actionPanelBottom?: (actions: InvoiceListingActions) => React.ReactNode;
  onSubscribeTotals: (calc: CalculatedTotalsByLevel, fields?: QuoteChargeListingEntity[]) => void;
  chargesSubmitRef: MutableRefObject<{ submit: () => ListingForm<QuoteChargeListingEntity> } | null>;
}

export const InvoiceDetailsListing = ({
  VOId,
  quote,
  dynamicsVOId,
  chargesSubmitRef,
  onSubscribeTotals,
  equipmentList = [],
  actionPanelBottom,
  showDealerProfitability = true,
}: Props) => {
  const [selectedImages, setSelectedImages] = useState<File[]>([]);

  const chargeService = useMemo(() => new ChargeListingService(), []);

  const { onDeleteEquipment, onUpdateEquipment, onCreateEquipment, isAnyActionInProgress } = useEquipmentActions();
  const { handleUploadImages } = useEquipmentAssets();

  const { handleOpen: openCustomChargeDialog } = useModal(ModalsKeys.CreateChargesEquipmentDialog);
  const { handleOpen: handleOpenChargesDialog, handleClose } = useModal(ModalsKeys.ChargesEquipmentDialog);
  const { handleOpen: handleOpenEquipmentDialog, handleClose: handleCloseEquipmentDialog } = useModal(
    ModalsKeys.CreateUpdateEquipmentDialog
  );

  const initialFields = useMemo(() => {
    if (!equipmentList.length) return [];

    const fields = quoteMapper.toListingRows(VOId, equipmentList, quote);
    onSubscribeTotals?.(getCalculatedTotalsByLevel(fields), fields);
    return fields;
  }, [VOId, equipmentList, onSubscribeTotals, quote]);

  const { form, columns, onRemove, tableData, handleInsertRow, handleRemoveRow, handleUpdateRow } = useListing<
    QuoteChargeListingEntity,
    QuoteChargeListingContext
  >(
    {
      headerColumns: invoiceDetailsListingColumns,
      bodyFields: initialFields,
    },
    {
      shouldUnregister: false,
      onSubscribe: value => {
        onSubscribeTotals?.(getCalculatedTotalsByLevel(value), value);
      },
    }
  );

  useImperativeHandle(chargesSubmitRef, () => ({
    submit: () => form.getValues(),
  }));

  const watchedRows = useWatch({ control: form.control, name: 'bodyFields' });

  const { data: serverCalcData, loading } = useQuery<
    {
      quoteCalc: QueryResponse<QuoteCalcViewModel>;
    },
    GraphqlResolverInput<QuoteCalcPreviewInput>
  >(quoteAPI.getQuoteCalcPreview, {
    skip: !watchedRows?.length,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        rows: quoteCalcMapper.toPreviewRows(watchedRows),
      },
    },
  });

  const serverCalc = serverCalcData?.quoteCalc?.result?.entity ?? null;

  const handleAddCharge = useCallback(
    (bucket: ChargeBucketRef) => {
      const bodyFieldsValue = form.getValues('bodyFields');

      const resolveInsertIndex = () => chargeService.getInsertIndex(bodyFieldsValue ?? [], bucket);

      handleOpenChargesDialog({
        onOpenCreateChargeDialog: () => {
          openCustomChargeDialog({
            onConfirm: newCharge => {
              const newRow = chargeService.createCustomChargeRow(bucket, newCharge.name);

              handleInsertRow(newRow, resolveInsertIndex());
              handleClose();
            },
          });
        },
        onConfirm: charges => {
          handleClose();
          let insertAt = resolveInsertIndex();

          charges.forEach(template => {
            const newRow = chargeService.createChargeRowFromTemplate(bucket, template);

            handleInsertRow(newRow, insertAt);
            insertAt += 1;
          });
        },
      });
    },
    [form, chargeService, handleClose, handleInsertRow, handleOpenChargesDialog, openCustomChargeDialog]
  );

  const handleAddEquipment = () => {
    handleOpenEquipmentDialog({
      equipmentId: '',
      onCancel: handleCloseEquipmentDialog,
      onImagesSaved: setSelectedImages,
      onSubmit: async ({ createDTO }) => {
        handleCloseEquipmentDialog();

        const equipmentId = await onCreateEquipment({
          vendorOpportunityId: VOId,
          dynamicsVendorOpportunityId: dynamicsVOId,
          ...createDTO,
        });

        // Upload images if any were selected
        if (selectedImages.length > 0 && equipmentId) {
          await handleUploadImages(equipmentId, 'IMAGE', selectedImages);
          setSelectedImages([]); // Clear after upload
        }

        handleInsertRow(quoteMapper.toListingRow({ equipmentId, ...createDTO }, quote), 0);
      },
    });
  };

  const handleEditEquipment = (equipmentId: string, rowIndex: number) => {
    handleOpenEquipmentDialog({
      equipmentId,
      onCancel: handleCloseEquipmentDialog,
      onSubmit: async ({ updateDTO }) => {
        handleCloseEquipmentDialog();

        await onUpdateEquipment({
          vendorOpportunityId: VOId,
          dynamicsVendorOpportunityId: dynamicsVOId,
          ...updateDTO,
        });

        handleUpdateRow(quoteMapper.toListingRow(updateDTO, quote), rowIndex);
      },
    });
  };

  const handleSyncEquipment = async (equipmentForUpdate: Nullable<QuoteEquipmentWithCharge>) => {
    if (!equipmentForUpdate || !equipmentForUpdate.equipmentId) return;

    const targetEquipment = equipmentList.find(({ equipmentId }) => equipmentId === equipmentForUpdate.equipmentId);

    if (!targetEquipment) return;

    const isNeedToUpdate = quoteMapper.hasEquipmentSyncChanges(equipmentForUpdate, targetEquipment);

    if (!isNeedToUpdate) return;

    await onUpdateEquipment({
      ...quoteMapper.toEquipmentSyncDTO(equipmentForUpdate),
      vendorOpportunityId: VOId,
      equipmentId: targetEquipment.equipmentId,
      attachments: targetEquipment.attachments,
      dynamicsVendorOpportunityId: dynamicsVOId,
    });
  };

  const handleRemoveEquipment = (equipmentId: string, rowIndex: number) => {
    onDeleteEquipment(
      {
        equipmentId,
        vendorOpportunityId: VOId,
        dynamicsVendorOpportunityId: dynamicsVOId,
      },
      () => {
        onRemove(rowIndex);
      }
    );
  };

  const groups = useMemo<Group<QuoteChargeListingEntity>[]>(() => {
    if (!tableData.length) return [];

    return chargeService.buildBuckets(tableData, VOId).map(bucket => ({
      name: bucket.id,
      data: bucket.data,
      beforeGroup: {
        cellStyles: groupHeaderCellStyles,
        render: () => <GroupHeader title={bucket.title} onAdd={() => handleAddCharge(bucket)} />,
      },
      afterGroup: {
        disableCellWrap: true,
        render: () => <GroupSubtotalRow data={bucket.data} columns={columns} priceCalc={serverCalc} />,
      },
    }));
  }, [VOId, tableData, chargeService, handleAddCharge, columns, serverCalc]);

  return (
    <FormProvider {...form}>
      <Stack spacing={2}>
        <Listing<QuoteChargeListingEntity, QuoteChargeListingContext>
          groups={groups}
          loading={loading}
          columns={columns}
          tableData={tableData}
          injectContext={{
            form,
            chargeService,
            invoiceId: VOId,
            priceCalc: serverCalc,
            onRemoveRow: handleRemoveRow,
            onEditEquipment: handleEditEquipment,
            onSyncEquipment: handleSyncEquipment,
            onRemoveEquipment: handleRemoveEquipment,
            isAnyEquipmentActionInProgress: isAnyActionInProgress,
          }}
          actionPanelBottom={() => actionPanelBottom?.({ onAddEquipment: handleAddEquipment })}
        />

        {showDealerProfitability && <DealerProfitability profitability={serverCalc?.profitability ?? null} />}
      </Stack>
    </FormProvider>
  );
};
